import { variantProvider } from '@/app'
import { ActionIcon, Button, EmptyPlaceholder, Text, View } from '@/components'
import { APIClient } from '@/services'
import { replyStore } from '@/stores'
import { CommunityComment } from '@/types'
import { useIsMobile } from '@/utils'
import { TypeGuards } from '@codeleap/common'
import { globalState } from '@codeleap/store'
import { AugmentedRenderItemInfo } from '@codeleap/web'
import React, { useCallback } from 'react'
import { createVirtualList } from '../VirtualList'
import { commentsDisabledItemMap } from './constants'
import { useCurrentFeedItem } from './feedScroll'
import { FeedActionBar } from './molecules/FeedActionBar'
import { CommentWithReplies } from './organisms/CommentWithReplies'
import { ReportModal } from './organisms/Report'

type FeedCommentListProps = {
  enabled?: boolean
}

export const feedCommentDrawer = globalState({
  visible: false,
  expanded: false,
})

const toggleExpanded = () => feedCommentDrawer.set({ expanded: !feedCommentDrawer.get().expanded })

const toggle = () => {
  feedCommentDrawer.set({ visible: !feedCommentDrawer.get()?.visible })
  replyStore.resetReplyState()
}

const close = () => {
  feedCommentDrawer.set({ visible: false })
  replyStore.resetReplyState()
}

const CommentVirtualizer = createVirtualList<CommunityComment, any>()

const estimateCommentSize = (comment: CommunityComment) => {
  if (!comment?.text) return 40
  return (comment.text.length / 24) * 16
}

export const FeedCommentList = ({ enabled }: FeedCommentListProps) => {
  const isExpanded = feedCommentDrawer.use(state => state.expanded)
  const me = APIClient.Session.useProfile()
  const currentItem = useCurrentFeedItem()
  const myProfileId = me?.data?.id

  const { listProps } = APIClient.Community.useCommunityComments(currentItem?.id as number, { enabled })

  const onReportComment = useCallback((item: CommunityComment) => {
    ReportModal.open({
      content: {
        comment: item.id,
      },
    })
  }, [])

  const renderComment = useCallback(
    (info: AugmentedRenderItemInfo<CommunityComment>) => {
      const canReport = !!myProfileId && info?.item?.profile?.id !== myProfileId

      const report = canReport ? () => onReportComment(info.item) : null

      const reply = (commentToReply?: CommunityComment) => {
        replyStore.setReplyTo(commentToReply || info.item)
      }

      return <CommentWithReplies comment={info.item} onReport={report} onReply={reply} />
    },
    [onReportComment, myProfileId],
  )

  const commentCount = currentItem?.comments

  const Header = useCallback(
    () => (
      <>
        <View variants={['padding:2']} style={styles.headerDesktop}>
          <Text
            text={listProps.loading || TypeGuards.isNil(commentCount) ? `Comments...` : `Comments (${commentCount})`}
            variants={['h3']}
          />
        </View>
        <View variants={['padding:2']} style={styles.headerMobile}>
          <ActionIcon
            debugName='FeedCommentList:expandCollapse'
            name={isExpanded ? 'condense-square' : 'expand-square'}
            onPress={toggleExpanded}
            variants={['minimal', 'neutral5', 'marginLeft:auto']}
          />
        </View>
      </>
    ),
    [commentCount, listProps.loading, isExpanded, toggleExpanded],
  )

  return (
    <View style={styles.listWrapper}>
      <CommentVirtualizer.Provider key={currentItem?.id} list={listProps} estimateSize={estimateCommentSize}>
        <Header />
        <CommentVirtualizer.Container
          style={styles.list}
          placeholder={{
            debugName: 'Comment list placeholder',
            // @ts-ignore
            icon: 'comment',
            title: `No comments yet`,
            description: `Start the conversation`,
            variants: ['feedList'],
          }}
        >
          <CommentVirtualizer.Items renderItem={renderComment} />
        </CommentVirtualizer.Container>
      </CommentVirtualizer.Provider>
    </View>
  )
}

export const FeedCommentDrawer = () => {
  const visible = feedCommentDrawer.use(s => s.visible)
  const currentItem = useCurrentFeedItem()
  const disabledComments = commentsDisabledItemMap[currentItem?.type] ?? false

  if (disabledComments) {
    return (
      <View css={[styles.drawer]} key={currentItem?.id}>
        <View variants={['flex', 'center']}>
          <View variants={['column', 'gap:2', 'center']}>
            <EmptyPlaceholder
              // @ts-ignore
              icon='comment'
              title='No comments'
              description='This type of content has no comments.'
              variants={['feedList']}
            />
            <Button variants={['fullWidth']} debugName='close drawer' text='Ok' onPress={toggle} />
          </View>
        </View>
      </View>
    )
  }

  return (
    <View css={[styles.drawer]} key={currentItem?.id}>
      <FeedCommentList enabled={visible} />
      <FeedActionBar />
    </View>
  )
}

FeedCommentDrawer.Container = (props: React.PropsWithChildren) => {
  const visible = feedCommentDrawer.use(s => s.visible)
  const expanded = feedCommentDrawer.use(s => s.expanded)

  const isMobile = useIsMobile()

  const onBackdropPress = () => {
    if (visible) {
      FeedCommentDrawer.toggle()
    }
  }

  return (
    <>
      {isMobile && visible ? (
        <div css={[styles.drawerBackdrop, visible && styles.drawerBackdropVisible]} onClick={onBackdropPress} />
      ) : null}
      <div
        css={[
          styles.drawerContainer,
          visible && styles.drawerContainerVisible,
          expanded && styles.drawerContainerExpanded,
        ]}
        {...props}
      >
        {props.children}
      </div>
    </>
  )
}

FeedCommentDrawer.toggle = toggle
FeedCommentDrawer.close = close
FeedCommentDrawer.useIsVisible = () => feedCommentDrawer.use(s => s.visible)

const DRAWER_WIDTH_DESKTOP = '25vw'

FeedCommentDrawer.WIDTH_DESKTOP = DRAWER_WIDTH_DESKTOP

const styles = variantProvider.createComponentStyle(theme => {
  return {
    headerDesktop: {
      [theme.media.down('small')]: {
        display: 'none',
      },
    },
    headerMobile: {
      [theme.media.up('small')]: {
        display: 'none',
      },
    },
    drawer: {
      backgroundColor: theme.colors.neutral1,
      width: '100%',
      [theme.media.down('small')]: {
        borderTopLeftRadius: theme.borderRadius.medium,
        borderTopRightRadius: theme.borderRadius.medium,
      },
      [theme.media.up('small')]: {
        width: DRAWER_WIDTH_DESKTOP,
        height: '100%',
        maxHeight: '100%',
      },
      alignSelf: 'stretch',
      ...theme.presets.column,
    },

    drawerContainer: {
      ...theme.presets.absolute,
      transition: `transform 0.2s linear, height 0.3s linear`,
      transform: `translateY(100dvh)`,
      bottom: 0,
      [theme.media.down('small')]: {
        zIndex: 999999 + 1,
        width: '100vw',
        height: '50dvh',
        position: 'fixed',
        right: 0,
        left: 0,
      },
      [theme.media.up('small')]: {
        zIndex: 9,
        transform: `translateX(${DRAWER_WIDTH_DESKTOP})`,
        right: 0,
        height: '100%',
      },
      ...theme.presets.row,
      alignItems: 'center',
    },
    drawerContainerVisible: {
      [theme.media.down('small')]: {
        transform: `translateY(0)`,
      },
      [theme.media.up('small')]: {
        transform: `translateX(0)`,
      },
    },
    drawerContainerExpanded: {
      [theme.media.down('small')]: {
        height: '90dvh',
      },
    },
    drawerBackdrop: {
      backgroundColor: '#000',
      opacity: 0,
      transition: 'opacity 0.2s linear',
    },
    drawerBackdropVisible: {
      opacity: 0.15,
    },
    listWrapper: {
      ...theme.presets.column,
      flex: 1,
      minHeight: 0,
    },
    list: {
      flex: 1,
      minHeight: 0,
    },
  }
}, true)
